import React, { useState } from "react";
import { useUser } from "../shared/UserContext";
import { SUPPORT_EMAIL, supportMailto, submitSupportMessage } from "../shared/support";

// Settings → Help. Two paths to reach support (Michael asked for both):
//   • Email us — a prefilled mailto to support@classboon.com.
//   • Send a message — writes to Firestore so it works even where mail is awkward
//     (shared/managed devices); the super-admin console reads them.
const CATEGORIES = ["General", "Bug / something broke", "Feature request", "Account / login", "Billing"];

export default function SupportSettings({ schoolName }) {
  const { user, role, schoolId, teacherName, displayName } = useUser();
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { ok: boolean, text: string }

  const name = displayName || teacherName || "";
  const mailto = supportMailto({ name, email: user?.email, schoolName, role, category });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setStatus(null);
    try {
      await submitSupportMessage({ user, role, schoolId, schoolName, category, message });
      setMessage("");
      setStatus({ ok: true, text: "Thanks — your message reached us. We read every one and will follow up by email if needed." });
    } catch (err) {
      console.error("Support message failed:", err);
      setStatus({ ok: false, text: "Couldn't send that. Please email us directly at " + SUPPORT_EMAIL + "." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>Help &amp; Support</h2>
      <p className="settings-intro">
        classBoon is built and supported by a teacher. If something's not working, you
        have a question, or you'd like to request a feature, reach out — we read every message.
      </p>

      <div className="settings-field-block" style={{ marginTop: 0 }}>
        <h3 style={{ marginTop: 0 }}>Email us</h3>
        <p className="settings-note" style={{ marginBottom: "var(--space-3)" }}>
          Opens your email app with your details filled in. Or write us directly at{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <a className="support-email-btn" href={mailto}>✉ Email support</a>
      </div>

      <div className="settings-field-block">
        <h3>Send us a message</h3>
        <p className="settings-note" style={{ marginBottom: "var(--space-3)" }}>
          Prefer not to leave the app? Send it here and we'll get it — no email needed.
        </p>
        <form onSubmit={handleSend}>
          <label htmlFor="support-cat" className="settings-label">Topic</label>
          <select
            id="support-cat"
            className="settings-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ width: "auto", marginBottom: "var(--space-3)" }}
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <label htmlFor="support-msg" className="settings-label">Message</label>
          <textarea
            id="support-msg"
            className="settings-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What were you trying to do, and what happened instead? Which page or feature?"
            rows={5}
            style={{ width: "100%", boxSizing: "border-box", resize: "vertical", marginBottom: "var(--space-3)" }}
          />

          <button type="submit" disabled={sending || !message.trim()}>
            {sending ? "Sending…" : "Send message"}
          </button>
          {status && (
            <p className={`settings-status ${status.ok ? "is-ok" : "is-error"}`}>{status.text}</p>
          )}
        </form>
      </div>
    </div>
  );
}
